import React, { useState } from "react";
import { motion } from "framer-motion";
import { Wand2, FileCode2, Fingerprint, Loader2, ArrowRightCircle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { Panel, Btn, Overline, RiskBadge } from "./ui";
import { useI18n, pickLang } from "../lib/i18n";

const RISK_TONE = {
  HIGH: "text-aegis-red border-aegis-red/40 bg-aegis-red/10",
  MEDIUM: "text-aegis-amber border-aegis-amber/40 bg-aegis-amber/10",
  LOW: "text-aegis-emerald border-aegis-emerald/40 bg-aegis-emerald/10",
};

export function CopilotActions({ seedIntent, onHandoff }) {
  const { t, lang } = useI18n();
  const [intent, setIntent] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(null);

  const value = intent || seedIntent || "";

  const makeDraft = async (nextKind) => {
    const text = (intent || seedIntent || "").trim();
    if (text.length < 4) {
      toast.error(t("act.needIntent"));
      return;
    }
    setBusy(nextKind);
    setDraft(null);
    try {
      const { data } = await http.post("/copilot/draft", { intent: text, kind: nextKind });
      setDraft(data);
      toast.success(`${data.draft_id} · ${t("act.drafted")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("act.failed"));
    } finally {
      setBusy(null);
    }
  };

  const handoff = async () => {
    if (!draft) return;
    setBusy("handoff");
    try {
      const { data } = await http.post("/copilot/handoff", { draft });
      toast.success(
        data.target === "policy_studio"
          ? t("act.toStudio", { id: data.draft.draft_id })
          : t("act.toGate", { id: data.action.req_id })
      );
      setDraft(null);
      setIntent("");
      onHandoff?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("act.failed"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel icon={Wand2} title={t("act.title")} subtitle={t("act.sub")} accent="purple" testid="copilot-actions-panel">
      <div className="space-y-4">
        <div>
          <Overline>{t("act.intent")}</Overline>
          <textarea
            value={value}
            onChange={(e) => setIntent(e.target.value)}
            rows={2}
            placeholder={t("act.placeholder")}
            data-testid="draft-intent-input"
            className="mt-2 w-full resize-none rounded-lg border border-edge well px-3.5 py-2.5 text-[13px] text-slate-100 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none focus:ring-1 focus:ring-slate-500"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Btn variant="purple" className="py-2.5" disabled={!!busy} onClick={() => makeDraft("policy")} testid="draft-policy-btn">
            {busy === "policy" ? <Loader2 size={15} className="animate-spin" /> : <FileCode2 size={15} />}
            {busy === "policy" ? t("act.drafting") : t("act.draftPolicy")}
          </Btn>
          <Btn variant="amber" className="py-2.5" disabled={!!busy} onClick={() => makeDraft("approval")} testid="draft-approval-btn">
            {busy === "approval" ? <Loader2 size={15} className="animate-spin" /> : <Fingerprint size={15} />}
            {busy === "approval" ? t("act.drafting") : t("act.draftApproval")}
          </Btn>
        </div>

        {draft && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl border border-aegis-purple/30 bg-aegis-purple/[0.04] p-4"
            data-testid="copilot-draft-card"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-[11px] text-aegis-purple">{draft.draft_id}</span>
              {draft.kind === "policy" ? (
                <span className={`rounded-md border px-2 py-0.5 font-mono text-[10px] ${RISK_TONE[draft.risk] || RISK_TONE.MEDIUM}`}>
                  {draft.risk}
                </span>
              ) : (
                <RiskBadge level={draft.risk_level} />
              )}
            </div>

            {draft.kind === "policy" ? (
              <>
                <p className="mt-2 text-[13px] font-medium text-slate-100">{pickLang(draft, "title", lang)}</p>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-400">{pickLang(draft, "rationale", lang)}</p>
                <pre
                  className="no-scrollbar mt-3 max-h-[220px] overflow-auto rounded-lg border border-edge well p-3 font-mono text-[11px] leading-relaxed text-aegis-emerald/90"
                  data-testid="draft-rego-preview"
                >
{draft.rego}
                </pre>
                {(draft[`checks_${lang}`] || []).length > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {draft[`checks_${lang}`].map((c, i) => (
                      <li key={c} className="flex gap-2 text-[12px] text-slate-300">
                        <span className="font-mono text-aegis-purple">{String(i + 1).padStart(2, "0")}</span>
                        {c}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                <p className="mt-2 font-mono text-[13px] text-aegis-amber">{draft.action_type}</p>
                <p className="mt-1 text-[13px] text-slate-200">{draft.scope}</p>
                <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">{pickLang(draft, "reason", lang)}</p>
                <div className="mt-3 space-y-1.5 rounded-lg border border-edge well px-3 py-2">
                  <p className="font-mono text-[10px] uppercase tracking-overline text-slate-400">
                    {t("act.disposition")}: <span className="text-aegis-amber">{draft.recommended_disposition}</span>
                  </p>
                  <p className="flex gap-2 text-[12px] text-slate-300">
                    <ShieldAlert size={13} className="mt-0.5 shrink-0 text-aegis-amber" />
                    {pickLang(draft, "blast_radius", lang)}
                  </p>
                </div>
              </>
            )}

            <Btn variant="solidCyan" className="mt-4 w-full py-2.5" disabled={busy === "handoff"} onClick={handoff} testid="draft-handoff-btn">
              {busy === "handoff" ? <Loader2 size={15} className="animate-spin" /> : <ArrowRightCircle size={15} />}
              {draft.kind === "policy" ? t("act.sendStudio") : t("act.sendGate")}
            </Btn>
          </motion.div>
        )}

        {!draft && <p className="text-[11px] leading-relaxed text-slate-400">{t("act.note")}</p>}
      </div>
    </Panel>
  );
}
